// Shared components for tool result display

export function StatusBadge({ status, label }: { status: "valid" | "invalid" | "unknown"; label?: string }) {
  const styles = {
    valid: "bg-green-500/10 text-green-600",
    invalid: "bg-red-500/10 text-red-600",
    unknown: "bg-amber-500/10 text-amber-600",
  };

  const icons = {
    valid: (
      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
      </svg>
    ),
    invalid: (
      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
      </svg>
    ),
    unknown: (
      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
      </svg>
    ),
  };

  const defaultLabels = {
    valid: "Valid",
    invalid: "Invalid",
    unknown: "Unknown",
  };

  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium ${styles[status]}`}>
      {icons[status]}
      {label || defaultLabels[status]}
    </span>
  );
}

export function SPFResult({ data }: { data: any }) {
  return (
    <div className="rounded-xl border bg-card p-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">SPF Record</h3>
        <StatusBadge status={data.valid ? "valid" : "invalid"} />
      </div>
      {data.record && (
        <code className="mt-3 block overflow-x-auto rounded-lg bg-muted p-3 text-sm">
          {data.record}
        </code>
      )}
      {data.lookupCount !== undefined && (
        <p className="mt-3 text-sm">
          DNS Lookups: <strong>{data.lookupCount}/10</strong>
          {data.lookupCount > 10 && (
            <span className="ml-2 text-red-600">(exceeds limit!)</span>
          )}
        </p>
      )}
      {data.includes?.length > 0 && (
        <div className="mt-3">
          <p className="text-sm font-medium">Includes:</p>
          <ul className="mt-1 text-sm text-muted-foreground">
            {data.includes.map((inc: string, i: number) => (
              <li key={i}>{inc}</li>
            ))}
          </ul>
        </div>
      )}
      {data.errors?.length > 0 && (
        <div className="mt-3">
          <p className="text-sm font-medium text-red-600">Errors:</p>
          <ul className="mt-1 text-sm text-red-600">
            {data.errors.map((err: string, i: number) => (
              <li key={i}>{err}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export function DKIMResult({ data }: { data: any }) {
  const isDiscovery = Array.isArray(data.selectors);

  if (isDiscovery) {
    return (
      <div className="rounded-xl border bg-card p-6">
        <h3 className="text-lg font-semibold">DKIM Discovery</h3>
        {data.selectors?.length > 0 ? (
          <div className="mt-4 space-y-3">
            {data.selectors.map((sel: any, i: number) => (
              <div key={i} className="rounded-lg bg-muted p-4">
                <div className="flex items-center justify-between">
                  <span className="font-medium">{sel.selector}</span>
                  <StatusBadge status={sel.valid ? "valid" : "invalid"} />
                </div>
                {sel.record && (
                  <code className="mt-2 block overflow-x-auto text-xs text-muted-foreground">
                    {sel.record.substring(0, 150)}...
                  </code>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="mt-3 text-muted-foreground">
            No common DKIM selectors found. Try entering a specific selector.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-xl border bg-card p-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">DKIM Record</h3>
        <StatusBadge status={data.valid ? "valid" : "invalid"} />
      </div>
      {data.record && (
        <code className="mt-3 block overflow-x-auto rounded-lg bg-muted p-3 text-sm">
          {data.record}
        </code>
      )}
      {data.error && (
        <p className="mt-2 text-sm text-red-600">{data.error}</p>
      )}
    </div>
  );
}

export function DMARCResult({ data }: { data: any }) {
  return (
    <div className="rounded-xl border bg-card p-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">DMARC Record</h3>
        <StatusBadge status={data.valid ? "valid" : "invalid"} />
      </div>
      {data.record && (
        <code className="mt-3 block overflow-x-auto rounded-lg bg-muted p-3 text-sm">
          {data.record}
        </code>
      )}
      {data.parsed && (
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <div className="rounded-lg bg-muted p-3">
            <div className="text-xs text-muted-foreground">Policy (p)</div>
            <div className="font-semibold">{data.parsed.p || "none"}</div>
          </div>
          <div className="rounded-lg bg-muted p-3">
            <div className="text-xs text-muted-foreground">Subdomain Policy (sp)</div>
            <div className="font-semibold">{data.parsed.sp || "inherit"}</div>
          </div>
          <div className="rounded-lg bg-muted p-3">
            <div className="text-xs text-muted-foreground">Percentage (pct)</div>
            <div className="font-semibold">{data.parsed.pct || "100"}%</div>
          </div>
          <div className="rounded-lg bg-muted p-3">
            <div className="text-xs text-muted-foreground">Aggregate Reports (rua)</div>
            <div className="truncate font-semibold">{data.parsed.rua || "not set"}</div>
          </div>
        </div>
      )}
      {data.warnings?.length > 0 && (
        <div className="mt-4">
          <p className="text-sm font-medium text-yellow-600">Warnings:</p>
          <ul className="mt-1 text-sm text-yellow-600">
            {data.warnings.map((w: string, i: number) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      )}
      {data.errors?.length > 0 && (
        <div className="mt-4">
          <p className="text-sm font-medium text-red-600">Errors:</p>
          <ul className="mt-1 text-sm text-red-600">
            {data.errors.map((e: string, i: number) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export function FullCheckResult({ data }: { data: any }) {
  return (
    <div className="space-y-4">
      {/* SPF */}
      <div className="rounded-xl border bg-card p-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">SPF Record</h3>
          <StatusBadge status={data.spf?.valid ? "valid" : "invalid"} />
        </div>
        {data.spf?.record && (
          <code className="mt-3 block overflow-x-auto rounded-lg bg-muted p-3 text-sm">
            {data.spf.record}
          </code>
        )}
        {data.spf?.error && (
          <p className="mt-2 text-sm text-red-600">{data.spf.error}</p>
        )}
      </div>

      {/* DKIM */}
      <div className="rounded-xl border bg-card p-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">DKIM Records</h3>
          <StatusBadge
            status={data.dkim?.selectors?.length > 0 ? "valid" : "unknown"}
            label={data.dkim?.selectors?.length > 0 ? `${data.dkim.selectors.length} found` : "Unknown"}
          />
        </div>
        {data.dkim?.selectors?.length > 0 ? (
          data.dkim.selectors.map((sel: any, i: number) => (
            <div key={i} className="mt-3 rounded-lg bg-muted p-3">
              <div className="text-sm font-medium">{sel.selector}</div>
              <code className="mt-1 block overflow-x-auto text-xs text-muted-foreground">
                {sel.record?.substring(0, 100)}...
              </code>
            </div>
          ))
        ) : (
          <p className="mt-3 text-sm text-muted-foreground">
            No common DKIM selectors found. Your domain may still have DKIM configured with a custom selector.
          </p>
        )}
      </div>

      {/* DMARC */}
      <div className="rounded-xl border bg-card p-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">DMARC Record</h3>
          <StatusBadge status={data.dmarc?.valid ? "valid" : "invalid"} />
        </div>
        {data.dmarc?.record && (
          <code className="mt-3 block overflow-x-auto rounded-lg bg-muted p-3 text-sm">
            {data.dmarc.record}
          </code>
        )}
        {data.dmarc?.policy && (
          <div className="mt-3 flex gap-4 text-sm">
            <span>Policy: <strong>{data.dmarc.policy}</strong></span>
            {data.dmarc.pct && <span>Percentage: <strong>{data.dmarc.pct}%</strong></span>}
          </div>
        )}
        {data.dmarc?.error && (
          <p className="mt-2 text-sm text-red-600">{data.dmarc.error}</p>
        )}
      </div>
    </div>
  );
}

export function SecurityScanResult({ data }: { data: any }) {
  const gradeColors: Record<string, string> = {
    "A+": "text-green-600 bg-green-500/10",
    "A": "text-green-600 bg-green-500/10",
    "B": "text-lime-600 bg-lime-500/10",
    "C": "text-amber-600 bg-amber-500/10",
    "D": "text-orange-600 bg-orange-500/10",
    "F": "text-red-600 bg-red-500/10",
  };

  const headerStatusIcon = (status: string) => {
    if (status === "pass") {
      return (
        <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
        </svg>
      );
    } else if (status === "warning") {
      return (
        <svg className="h-5 w-5 text-amber-500" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
        </svg>
      );
    }
    return (
      <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
      </svg>
    );
  };

  return (
    <div className="space-y-4">
      {/* Overall Score */}
      <div className="rounded-xl border bg-card p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">Security Score</h3>
            <p className="text-sm text-muted-foreground">{data.domain}</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="text-3xl font-bold">{data.overallScore}</div>
              <div className="text-sm text-muted-foreground">out of 100</div>
            </div>
            <div className={`rounded-lg px-4 py-2 text-2xl font-bold ${gradeColors[data.grade] || "text-gray-600 bg-gray-500/10"}`}>
              {data.grade}
            </div>
          </div>
        </div>
        {data.cached && (
          <p className="mt-2 text-xs text-muted-foreground">
            Cached result from {new Date(data.scannedAt).toLocaleString()}
          </p>
        )}
      </div>

      {/* SSL/TLS */}
      <div className="rounded-xl border bg-card p-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">SSL/TLS Certificate</h3>
          <StatusBadge status={data.ssl.valid ? "valid" : "invalid"} />
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <div className="rounded-lg bg-muted p-3">
            <div className="text-xs text-muted-foreground">Issuer</div>
            <div className="font-semibold truncate">{data.ssl.issuer || "Unknown"}</div>
          </div>
          <div className="rounded-lg bg-muted p-3">
            <div className="text-xs text-muted-foreground">Protocol</div>
            <div className="font-semibold">{data.ssl.protocol || "Unknown"}</div>
          </div>
          <div className="rounded-lg bg-muted p-3">
            <div className="text-xs text-muted-foreground">Expires</div>
            <div className="font-semibold">
              {data.ssl.expiresAt
                ? new Date(data.ssl.expiresAt).toLocaleDateString()
                : "Unknown"}
            </div>
          </div>
          <div className="rounded-lg bg-muted p-3">
            <div className="text-xs text-muted-foreground">Days Until Expiry</div>
            <div className={`font-semibold ${
              data.ssl.daysUntilExpiry !== null && data.ssl.daysUntilExpiry < 30
                ? "text-amber-600"
                : ""
            }`}>
              {data.ssl.daysUntilExpiry ?? "Unknown"}
            </div>
          </div>
        </div>
        {data.ssl.errors?.length > 0 && (
          <div className="mt-3 text-sm text-red-600">
            {data.ssl.errors.map((e: string, i: number) => <p key={i}>{e}</p>)}
          </div>
        )}
        {data.ssl.warnings?.length > 0 && (
          <div className="mt-3 text-sm text-amber-600">
            {data.ssl.warnings.map((w: string, i: number) => <p key={i}>{w}</p>)}
          </div>
        )}
      </div>

      {/* Security Headers */}
      <div className="rounded-xl border bg-card p-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Security Headers</h3>
          <span className="text-sm text-muted-foreground">
            Score: {data.headers.score}/100
          </span>
        </div>
        <div className="mt-4 space-y-2">
          {data.headers.headers?.map((header: any, i: number) => (
            <div key={i} className="flex items-start gap-3 rounded-lg bg-muted p-3">
              <div className="mt-0.5">{headerStatusIcon(header.status)}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{header.name}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded ${
                    header.importance === "critical" ? "bg-red-500/10 text-red-600" :
                    header.importance === "high" ? "bg-orange-500/10 text-orange-600" :
                    header.importance === "medium" ? "bg-amber-500/10 text-amber-600" :
                    "bg-gray-500/10 text-gray-600"
                  }`}>
                    {header.importance}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">{header.description}</p>
                {header.status !== "pass" && header.recommendation && (
                  <p className="mt-1 text-xs text-amber-600">{header.recommendation}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Blacklist Status */}
      <div className="rounded-xl border bg-card p-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Blacklist Status</h3>
          <StatusBadge
            status={data.blacklist.clean ? "valid" : "invalid"}
            label={data.blacklist.clean ? "Clean" : "Listed"}
          />
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          Checked {data.blacklist.checked?.length || 0} blocklists
        </p>
        {data.blacklist.detectedOn?.length > 0 && (
          <div className="mt-3 rounded-lg bg-red-500/10 p-3 text-sm text-red-600">
            <p className="font-medium">Listed on:</p>
            <ul className="mt-1 list-disc list-inside">
              {data.blacklist.detectedOn.map((list: string, i: number) => (
                <li key={i}>{list}</li>
              ))}
            </ul>
          </div>
        )}
        {data.blacklist.errors?.length > 0 && (
          <div className="mt-3 text-sm text-amber-600">
            {data.blacklist.errors.map((e: string, i: number) => <p key={i}>{e}</p>)}
          </div>
        )}
      </div>
    </div>
  );
}
